import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getEmployees, clockIn, clockOut, manualEntry, getTimeEntries, deleteEntry, getActiveEntry } from '../api';
import { formatDuration, formatTimer, formatDateTime, getInitials, toLocalDatetimeInput } from '../utils';
import { useAuth } from '../AuthContext';
import ConfirmDialog from '../components/ConfirmDialog';

export default function TimeTracker() {
  const { user, isAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState('');
  const [activeEntry, setActiveEntry] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [entries, setEntries] = useState([]);
  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState({ clock_in: '', clock_out: '', note: '' });
  const [confirm, setConfirm] = useState(null); // { type: 'in'|'out' }
  const timerRef = useRef(null);

  const loadEmployees = useCallback(async () => {
    if (!isAdmin && user?.employee_id) {
      // Employees are locked to themselves
      const emps = await getEmployees();
      const self = emps.find(e => e.id === user.employee_id);
      setEmployees(self ? [self] : []);
      setSelectedEmp(user.employee_id);
      return;
    }
    const emps = await getEmployees();
    setEmployees(emps);
    if (emps.length && !selectedEmp) setSelectedEmp(emps[0].id);
  }, [isAdmin, user?.employee_id, selectedEmp]);

  const loadEntries = useCallback(async () => {
    if (!selectedEmp) return;
    const from = new Date(Date.now() - 30 * 86400000).toISOString();
    const data = await getTimeEntries({ employee_id: selectedEmp, from });
    setEntries(data);
  }, [selectedEmp]);

  const loadActive = useCallback(async () => {
    if (!selectedEmp) return;
    const entry = await getActiveEntry(selectedEmp);
    setActiveEntry(entry);
    if (entry) {
      const secs = Math.floor((Date.now() - new Date(entry.clock_in)) / 1000);
      setElapsed(secs);
    } else {
      setElapsed(0);
    }
  }, [selectedEmp]);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);
  useEffect(() => { if (selectedEmp) { loadActive(); loadEntries(); } }, [selectedEmp, loadActive, loadEntries]);

  useEffect(() => {
    if (activeEntry) {
      timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [activeEntry]);

  const doClockIn = async () => {
    setConfirm(null);
    try {
      const entry = await clockIn(selectedEmp);
      await loadActive();
      await loadEntries();
      if (window.__startAutoCapture) window.__startAutoCapture(entry?.id || null);
    } catch (e) {
      if (e.response?.status === 409) alert('Already clocked in');
    }
  };

  const doClockOut = async () => {
    setConfirm(null);
    if (window.__stopAutoCapture) window.__stopAutoCapture();
    // Retry up to 3 times if clock-out fails (backend may be momentarily unavailable)
    let success = false;
    for (let i = 0; i < 3; i++) {
      try {
        await clockOut(selectedEmp);
        success = true;
        break;
      } catch (e) {
        if (i < 2) await new Promise(r => setTimeout(r, 2000));
      }
    }
    if (!success) {
      // Store pending clock-out in localStorage to retry on next load
      const pending = JSON.parse(localStorage.getItem('pending_clockout') || '[]');
      pending.push({ employee_id: selectedEmp, timestamp: new Date().toISOString() });
      localStorage.setItem('pending_clockout', JSON.stringify(pending));
      alert('Clock-out saved locally. It will sync automatically when connection is restored.');
    }
    await loadActive();
    await loadEntries();
    if (window.__refreshActiveEntry) window.__refreshActiveEntry();
  };

  const handleClockIn = () => setConfirm({ type: 'in' });
  const handleClockOut = () => setConfirm({ type: 'out' });

  const handleManual = async (e) => {
    e.preventDefault();
    await manualEntry({ employee_id: selectedEmp, ...manual });
    setShowManual(false);
    setManual({ clock_in: '', clock_out: '', note: '' });
    await loadEntries();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    await deleteEntry(id);
    await loadEntries();
  };

  const emp = employees.find(e => e.id === selectedEmp);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Time Tracker</h1>
        <button className="btn btn-outline" onClick={() => setShowManual(true)}>+ Manual Entry</button>
      </div>

      <div className="grid-2" style={{ marginBottom: 20 }}>
        <div className="card">
          <div className="card-title">{isAdmin ? 'Select Employee' : 'My Time'}</div>
          {isAdmin && (
            <div className="form-group">
              <select value={selectedEmp} onChange={e => setSelectedEmp(e.target.value)}>
                {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
          )}

          {emp && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div className="employee-avatar" style={{ width: 48, height: 48, fontSize: '1rem' }}>{getInitials(emp.name)}</div>
              <div>
                <div style={{ fontWeight: 600 }}>{emp.name}</div>
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>{emp.department} · {emp.role}</div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              className="btn btn-success"
              onClick={handleClockIn}
              disabled={!!activeEntry}
              style={{ flex: 1, justifyContent: 'center' }}
            >
              ▶ Clock In
            </button>
            <button
              className="btn btn-danger"
              onClick={handleClockOut}
              disabled={!activeEntry}
              style={{ flex: 1, justifyContent: 'center' }}
            >
              ■ Clock Out
            </button>
          </div>
        </div>

        <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          {activeEntry ? (
            <>
              <div className="badge badge-green" style={{ marginBottom: 12, fontSize: '0.875rem' }}>● Recording</div>
              <div className={`timer-display timer-pulse`}>{formatTimer(elapsed)}</div>
              <div className="text-muted mt-4" style={{ fontSize: '0.8rem' }}>
                Started at {new Date(activeEntry.clock_in).toLocaleTimeString()}
              </div>
            </>
          ) : (
            <>
              <div className="badge badge-gray" style={{ marginBottom: 12, fontSize: '0.875rem' }}>○ Idle</div>
              <div className="timer-display" style={{ color: 'var(--muted)' }}>00:00:00</div>
              <div className="text-muted mt-4" style={{ fontSize: '0.8rem' }}>Clock in to start tracking</div>
            </>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-title">Recent Entries</div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Date</th><th>Clock In</th><th>Clock Out</th><th>Duration</th><th>Note</th><th>Type</th><th></th></tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr><td colSpan="7" className="empty">No entries yet</td></tr>
              )}
              {entries.map(entry => (
                <tr key={entry.id}>
                  <td>{new Date(entry.clock_in).toLocaleDateString()}</td>
                  <td>{new Date(entry.clock_in).toLocaleTimeString()}</td>
                  <td>{entry.clock_out ? new Date(entry.clock_out).toLocaleTimeString() : <span className="badge badge-green">Active</span>}</td>
                  <td>{entry.duration_seconds ? formatDuration(entry.duration_seconds) : '—'}</td>
                  <td>{entry.note || '—'}</td>
                  <td><span className={`badge ${entry.manual ? 'badge-orange' : 'badge-blue'}`}>{entry.manual ? 'Manual' : 'Auto'}</span></td>
                  <td>
                    {!activeEntry || entry.id !== activeEntry.id ? (
                      <button className="btn btn-outline btn-sm" onClick={() => handleDelete(entry.id)}>Delete</button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        open={confirm?.type === 'in'}
        icon="▶️"
        title="Clock In"
        message={`Start a new session for ${emp?.name || 'this employee'}? Your screen will be captured according to admin settings.`}
        confirmLabel="Clock In"
        confirmClass="btn-success"
        onConfirm={doClockIn}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm?.type === 'out'}
        icon="⏹️"
        title="Clock Out"
        message={`End the current session for ${emp?.name || 'this employee'}? Total time will be saved.`}
        confirmLabel="Clock Out"
        confirmClass="btn-danger"
        onConfirm={doClockOut}
        onCancel={() => setConfirm(null)}
      />

      {showManual && (
        <div className="modal-overlay" onClick={() => setShowManual(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Manual Time Entry</div>
            <form onSubmit={handleManual}>
              <div className="form-group">
                <label>Employee</label>
                <select value={selectedEmp} onChange={e => setSelectedEmp(e.target.value)}>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label>Clock In</label>
                  <input type="datetime-local" value={manual.clock_in} onChange={e => setManual(m => ({ ...m, clock_in: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label>Clock Out</label>
                  <input type="datetime-local" value={manual.clock_out} onChange={e => setManual(m => ({ ...m, clock_out: e.target.value }))} required />
                </div>
              </div>
              <div className="form-group">
                <label>Note</label>
                <input type="text" value={manual.note} onChange={e => setManual(m => ({ ...m, note: e.target.value }))} placeholder="Optional note" />
              </div>
              <div className="flex gap-8" style={{ justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-outline" onClick={() => setShowManual(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save Entry</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
